import axios from 'axios';


const RATE_LIMIT_KEY = 'rateLimit:sensitiveOp:unlockAt';
const SHOP_BOOTSTRAP_CACHE_KEY = 'shop:bootstrap:lite:v1';
const GET_DEDUPE_TTL_MS = 1200;
const DEFAULT_API_TIMEOUT_MS = 30000;
const DEFAULT_CRITICAL_API_TIMEOUT_MS = 60000;
const inflightGetRequests = new Map();
const recentGetResponses = new Map();
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const parseTimeoutEnv = (rawValue, fallback) => {
  const parsed = Number.parseInt(String(rawValue ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const API_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_API_TIMEOUT_MS, DEFAULT_API_TIMEOUT_MS);
const CRITICAL_API_TIMEOUT_MS = Math.max(
  API_TIMEOUT_MS,
  parseTimeoutEnv(process.env.REACT_APP_API_CRITICAL_TIMEOUT_MS, DEFAULT_CRITICAL_API_TIMEOUT_MS)
);

const stableSerialize = (value) => {
  if (value == null) return '';
  if (Array.isArray(value)) return `[${value.map((item) => stableSerialize(item)).join(',')}]`;
  if (typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${key}:${stableSerialize(value[key])}`).join(',')}}`;
  }
  return String(value);
};

const buildGetRequestKey = (config = {}) => {
  const url = String(config.url || '');
  const method = String(config.method || 'get').toLowerCase();
  const params = stableSerialize(config.params || {});
  return `${method}:${url}?${params}`;
};

const clearExpiredGetCacheEntries = () => {
  const now = Date.now();
  for (const [key, entry] of recentGetResponses.entries()) {
    if (!entry || (now - Number(entry.cachedAt || 0)) > GET_DEDUPE_TTL_MS) {
      recentGetResponses.delete(key);
    }
  }
};

const shouldUseCriticalTimeout = (url) => {
  const path = String(url || '').toLowerCase();
  if (!path) return false;
  return path.includes('/shop/bootstrap')
    || path.includes('/auth/login')
    || path.includes('/auth/verify');
};

const resolveRequestTimeout = (config = {}) => {
  const configuredTimeout = Number(config.timeout);
  if (Number.isFinite(configuredTimeout) && configuredTimeout > 0) {
    return configuredTimeout;
  }
  if (shouldUseCriticalTimeout(config.url)) {
    return CRITICAL_API_TIMEOUT_MS;
  }
  return API_TIMEOUT_MS;
};

const emitGlobalToast = (message, severity = 'info') => {
  if (typeof window === 'undefined') return;
  const text = String(message || '').trim();
  if (!text) return;
  window.dispatchEvent(new CustomEvent('app:notify', {
    detail: { message: text, severity }
  }));
};

const isPermissionDenied = (error) => Number(error?.response?.status) === 403;

export const isAccessDeniedError = (error) => !!(error && (error.isAccessDenied || Number(error?.response?.status) === 403));

const shouldToastForMethod = (config) => {
  const method = String(config?.method || '').toLowerCase();
  if (method !== 'post' && method !== 'put') return false;
  if (config?.meta?.suppressToast === true) return false;
  if (config?.headers?.['x-suppress-toast'] === '1') return false;
  return true;
};

const isReadOnlyMethod = (config) => {
  const method = String(config?.method || 'get').toLowerCase();
  return method === 'get' || method === 'head' || method === 'options';
};

const buildSuccessToastMessage = (response) => {
  const data = response?.data;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
  const method = String(response?.config?.method || '').toUpperCase();
  return `${method} request completed successfully`;
};

const buildErrorToastMessage = (error) => {
  if (isPermissionDenied(error)) return 'Access denied';
  const data = error?.response?.data;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
  if (typeof error?.message === 'string' && error.message.trim()) return error.message.trim();
  const method = String(error?.config?.method || '').toUpperCase() || 'Request';
  return `${method} request failed`;
};

const getInitializationRedirectUrl = (response) => {
  const redirectPath = response?.data?.redirect || '/initialization';
  const hasExplicitApiBase = !!process.env.REACT_APP_API_URL;

  if (!hasExplicitApiBase && typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && String(window.location.port || '') === '56001') {
      return `http://localhost:56000${redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`}`;
    }
  }

  const fallbackApiBase = typeof window !== 'undefined'
    ? `${window.location.origin}/api`
    : '/api';
  const apiBase = process.env.REACT_APP_API_URL || fallbackApiBase;

  if (/^https?:\/\//i.test(redirectPath)) {
    return redirectPath;
  }

  try {
    const apiUrl = new URL(apiBase);
    const isLocalHost = apiUrl.hostname === 'localhost' || apiUrl.hostname === '127.0.0.1';
    const protocol = isLocalHost ? 'http:' : apiUrl.protocol;
    const backendOrigin = `${protocol}//${apiUrl.host}`;
    return `${backendOrigin}${redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`}`;
  } catch (_) {
    return redirectPath;
  }
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api'),
  timeout: API_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
  },
  validateStatus: (status) => {
    if (status === 304) return true; // treat cached responses as success instead of throwing
    return status >= 200 && status < 300;
  }
});

const baseRequest = api.request.bind(api);
api.request = (configOrUrl, maybeConfig) => {
  const config = typeof configOrUrl === 'string'
    ? { ...(maybeConfig || {}), url: configOrUrl }
    : { ...(configOrUrl || {}) };
  config.timeout = resolveRequestTimeout(config);
  const method = String(config.method || 'get').toLowerCase();
  const shouldDedupe = (method === 'get' || method === 'head')
    && config?.meta?.disableGetDedupe !== true;

  if (!shouldDedupe) {
    return baseRequest(config);
  }

  clearExpiredGetCacheEntries();
  const cacheKey = buildGetRequestKey(config);
  const cached = recentGetResponses.get(cacheKey);
  if (cached && (Date.now() - cached.cachedAt) <= GET_DEDUPE_TTL_MS) {
    return Promise.resolve(cached.response);
  }

  if (inflightGetRequests.has(cacheKey)) {
    return inflightGetRequests.get(cacheKey);
  }

  const requestPromise = baseRequest(config)
    .then((response) => {
      recentGetResponses.set(cacheKey, {
        cachedAt: Date.now(),
        response
      });
      return response;
    })
    .finally(() => {
      inflightGetRequests.delete(cacheKey);
    });

  inflightGetRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

if (typeof window !== 'undefined') {
  // Fully-online mode: clear any stale offline queue entries from prior sessions.
  try {
    window.localStorage.removeItem('offline:outbox:v1');
  } catch (_) {
    // ignore storage access issues
  }
}

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    if (!config.headers) {
      config.headers = {};
    }

    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get' && method !== 'head') {
      try {
        const stored = localStorage.getItem(RATE_LIMIT_KEY);
        if (stored) {
          const unlockAt = parseInt(stored, 10);
          if (!Number.isNaN(unlockAt)) {
            const now = Date.now();
            if (unlockAt > now) {
              await delay(Math.max(unlockAt - now, 0));
            } else {
              localStorage.removeItem(RATE_LIMIT_KEY);
            }
          }
        }
      } catch (_) {
        // ignore storage access issues
      }
    }

    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    if (shouldToastForMethod(response?.config)) {
      emitGlobalToast(buildSuccessToastMessage(response), 'success');
    }
    return response;
  },
  async (error) => {
    const accessDenied = isPermissionDenied(error);

    if (isPermissionDenied(error)) {
      // Keep backend ACL strict, but avoid noisy toasts for read-only/background UI fetches.
      if (!isReadOnlyMethod(error?.config)) {
        emitGlobalToast('Access denied', 'error');
      }
      error.message = 'Access denied';
      if (error.response && error.response.data) {
        error.response.data.message = 'Access denied';
        error.response.data.ui_suppressed = true;
      }
      error.isAccessDenied = true;
      error.suppressUiError = true;
    }

    if (!accessDenied && shouldToastForMethod(error?.config)) {
      emitGlobalToast(buildErrorToastMessage(error), 'error');
    }

    const status = error.response?.status;
    if (status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      try {
        window.sessionStorage.removeItem(SHOP_BOOTSTRAP_CACHE_KEY);
      } catch (_) {
        // ignore storage errors
      }
      const sessionReplaced = String(error?.response?.data?.code || '').toUpperCase() === 'SESSION_REPLACED';
      if (sessionReplaced) {
        emitGlobalToast('Your account was signed in on another device. Please login again.', 'warning');
      }
      window.location.href = '/login';
    }
    else if (status === 503 && error.response?.data?.code === 'NOT_INITIALIZED') {
      window.location.href = getInitializationRedirectUrl(error.response);
      return Promise.reject(error);
    }
    else if (status === 429) {
      const headers = error.response?.headers || {};
      const retryAfterHeader = headers['retry-after'] || headers['Retry-After'];
      let retryAfterSeconds = 0;
      if (retryAfterHeader) {
        const parsed = parseInt(retryAfterHeader, 10);
        if (!Number.isNaN(parsed)) retryAfterSeconds = parsed;
      } else if (typeof error.response?.data?.retry_after === 'number') {
        retryAfterSeconds = error.response.data.retry_after;
      }
      if (!Number.isFinite(retryAfterSeconds) || retryAfterSeconds <= 0) {
        retryAfterSeconds = 15; // sane fallback
      }
      const unlockAt = Date.now() + (retryAfterSeconds * 1000);
      try {
        localStorage.setItem(RATE_LIMIT_KEY, String(unlockAt));
        window.dispatchEvent(new CustomEvent('rate-limit', {
          detail: {
            key: 'sensitiveOp',
            unlockAt,
            retryAfterSeconds
          }
        }));
      } catch (_) {
        // ignore storage errors
      }

      const originalConfig = error.config;
      if (originalConfig && !originalConfig.__isRetry) {
        originalConfig.__isRetry = true;
        await delay(retryAfterSeconds * 1000);
        return api(originalConfig);
      }
    }
    return Promise.reject(error);
  }
);


// Helper to fetch routers (used in Network and Customers)
api.getRouters = function() {
  return api.get('/routers');
};

export default api;
